import { FC, ReactNode } from "react";

import { Box, EmptyState, EmptyStateProps } from "@hightouch/htui";

import PlaceholderSrc from "src/assets/placeholders/generic.svg";
import ErrorSrc from "src/assets/placeholders/error.png";
import { Markdown } from "src/ui/markdown";

export type PlaceholderContent = {
  image?: string;
  title?: string;
  body?: string | ReactNode;
  button?: ReactNode;
  error?: string;
  /** Call to action for the error state, the way `button` is for the empty one. */
  errorActions?: ReactNode;
  custom?: ReactNode;
  customError?: ReactNode;
  p?: number;
} & Pick<EmptyStateProps, "titleMarginProps" | "variant">;

type PlaceholderProps = {
  error?: boolean;
  content: PlaceholderContent | undefined;
};

export const Placeholder: FC<Readonly<PlaceholderProps>> = ({
  error,
  content,
}) => {
  if (error) {
    return (
      <EmptyState
        imageUrl={ErrorSrc}
        title="Error"
        titleMarginProps={content?.titleMarginProps}
        variant={content?.variant}
        actions={content?.errorActions}
        message={
          content?.customError || (
            <Box
              textAlign="center"
              maxW="72ch"
              color="text.danger"
              sx={{
                ":first-letter": {
                  textTransform: "capitalize",
                },
              }}
            >
              <Markdown underlineLinks>
                {content?.error || defaultError}
              </Markdown>
            </Box>
          )
        }
      />
    );
  }
  if (content?.custom) {
    return <>{content.custom}</>;
  }
  return (
    <EmptyState
      variant={content?.variant}
      title={content?.title}
      titleMarginProps={content?.titleMarginProps}
      imageUrl={content?.image ?? PlaceholderSrc}
      message={content?.body}
      actions={content?.button}
    />
  );
};

const defaultError = "Results were unable to load, please try again.";
